import { Tree, TreeApi } from "react-arborist";
import { ServiceNode } from "../../services/servicesConfigAdvanced";
import React, { useRef, useEffect } from "react";
import {
  Zap,
  Smartphone,
  Share2,
  Globe,
  Settings,
  Plug,
  Circle,
} from "lucide-react";
import {
  BarChart3,
  FileText,
  FilePlus,
  Folder,
  Calendar,
  Hash,
} from "lucide-react";
interface Props {
  data: ServiceNode[];
  onSelect: (id: string) => void;
  soonLabel?: string;
}

const SidebarTree: React.FC<Props> = ({ data, onSelect, soonLabel = "Soon" }) => {
  const treeRef = useRef<TreeApi<ServiceNode>>(null);

  // 🔥 Collapse everything on first render
  useEffect(() => {
    setTimeout(() => {
      treeRef.current?.visibleNodes.forEach((node) => {
        if (node.isOpen) {
          node.close();
        }
      });
    }, 0);
  }, []);

  const collapseAll = () => {
    treeRef.current?.visibleNodes.forEach((n) => {
      if (n.isOpen) n.close();
    });
  };
  const getChildIcon = (node: any) => {
    const id = node.id.toLowerCase();

    // Analytics / Reports
    if (id.includes("analytics") || id.includes("report"))
      return <BarChart3 size={14} />;

    // Create / New
    if (id.includes("create") || id.includes("new"))
      return <FilePlus size={14} />;

    // Calendar / Planning
    if (id.includes("calendar")) return <Calendar size={14} />;

    // Hashtag / SEO
    if (id.includes("hashtag") || id.includes("seo")) return <Hash size={14} />;

    // Settings / Tools
    if (id.includes("settings")) return <Settings size={14} />;

    // Internal container child
    if (node.isInternal) return <Folder size={14} />;

    // Default leaf
    return <FileText size={14} />;
  };

  const getServiceIcon = (node: any) => {
    const { id, level } = node;

    if (level === 0) {
      switch (id) {
        case "automation":
          return <Zap size={18} />;
        case "mobileApps":
          return <Smartphone size={18} />;
        case "social":
          return <Share2 size={18} />;
        case "website":
          return <Globe size={18} />;
        case "tools":
          return <Settings size={18} />;
        case "integrations":
          return <Plug size={18} />;
        default:
          return <Circle size={8} />;
      }
    }

    // For ALL children → subtle dot
    return <Circle size={6} />;
  };
  const sortBySeq = (nodes: ServiceNode[]): ServiceNode[] => {
    return [...nodes]
      .sort((a, b) => (a.seq ?? 999) - (b.seq ?? 999))
      .map((node) => ({
        ...node,
        children: node.children ? sortBySeq(node.children) : undefined,
      }));
  };
  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Tree<ServiceNode>
        ref={treeRef}
        data={sortBySeq(data)}
        width="100%"
        height={600}
        indent={24}
        rowHeight={38}
      >
        {({ node, style }) => {
          const level = node.level;
          const status = node.data.status;
          const isComingSoon = status === "coming-soon";

          // Hide Level 3+ (User requirement)
          if (level >= 3) return null;

          return (
            <div
              style={{
                ...style,
                display: "flex",
                alignItems: "center",
                padding: "6px 12px",
                paddingLeft: 12 + level * 16,
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: level === 0 ? 600 : 500,
                background: node.isSelected ? "#EEF1FF" : "transparent",
                borderRadius: "6px",
                opacity: isComingSoon ? 0.7 : 1,
                position: "relative"
              }}
              onClick={() => {
                if (node.id === "integrations") {
                  onSelect(node.id);
                  return;
                }
                
                if (isComingSoon) {
                  onSelect(node.id); // Still notify parent so it can show placeholder
                  return;
                }

                if (node.isInternal) {
                  if (node.isOpen) {
                    node.close();
                  } else {
                    treeRef.current?.visibleNodes.forEach((n) => {
                      if (n.isOpen && n.level === level) n.close();
                    });
                    node.open();
                  }
                } else {
                  onSelect(node.id);
                }
              }}
            >
              {/* Service Icon */}
              <span style={{ marginRight: 8, display: "flex", alignItems: "center" }}>
                {node.level === 0 ? getServiceIcon(node) : getChildIcon(node)}
              </span>

              {/* Label */}
              <span style={{ flex: 1 }}>{node.data.label}</span>

              {/* Coming Soon Badge */}
              {isComingSoon && (
                <span style={{
                  fontSize: "9px",
                  background: "#714B67",
                  color: "white",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  marginLeft: "8px"
                }}>
                  {soonLabel}
                </span>
              )}
            </div>
          );
        }}
      </Tree>
    </div>
  );
};

export default SidebarTree;
